import { useEffect, useRef } from 'react';
import { useBlocker } from 'react-router';

/**
 * in-app 導覽離頁防呆：useBlocker + 既有 confirm（ConfirmDialog）。
 * 確認離開 → proceed（呼叫端負責保留暫存、不 discard）；取消 → reset 留頁。
 *
 * @param {object} options
 * @param {boolean | import('react-router').BlockerFunction} options.when
 * @param {(opts: object) => Promise<boolean>} options.confirm — useConfirmDialog().confirm
 * @param {string} [options.title]
 * @param {string} [options.message]
 * @param {string} [options.confirmText]
 * @param {string} [options.cancelText]
 */
export function useLeaveConfirm({
  when,
  confirm,
  title = '離開頁面',
  message = '表單尚未送出，確定要離開？',
  confirmText = '確定離開',
  cancelText = '取消',
}) {
  const blocker = useBlocker(when);
  const busyRef = useRef(false);

  useEffect(() => {
    if (blocker.state !== 'blocked' || busyRef.current) {
      return undefined;
    }
    if (typeof confirm !== 'function') {
      blocker.reset?.();
      return undefined;
    }

    let cancelled = false;
    busyRef.current = true;

    (async () => {
      try {
        const ok = await confirm({
          title,
          message,
          confirmText,
          cancelText,
        });
        if (cancelled) {
          return;
        }
        if (ok) {
          blocker.proceed?.();
        } else {
          blocker.reset?.();
        }
      } catch {
        if (!cancelled) {
          blocker.reset?.();
        }
      } finally {
        busyRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [blocker, cancelText, confirm, confirmText, message, title]);
}

export default useLeaveConfirm;
